import {
   EnumIntegrationConnectorMessages,
   IntegrationConnectorFields, RefIntegrationSettingsDocument, useRefIntegrationMessageUpdateMutation, useRefIntegrationSettingsQuery, useRefIntegrationSettingUpdateMutation,
} from "../../../Queries";
import {SavingUI} from "../../../SavingProvider";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, CircularProgress} from "@mui/material";
import PageContent from "../../../layout/PageContent";
import {BodyText, TitleText} from "../../../layout/Typography";
import * as React from "react";
import PageContentTabs, {PageTab} from "../../../layout/PageContentTabs";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {SystemMessages} from "./RefSystemMessages";
import {SettingsEditor} from "./RefIntegrationSettingsEditor";
import {SettingsLine} from "./RefIntegrationSettingsType";
import {useRefConsumerActivitySetupMutation} from "../../../Queries";

type RefIntegrationSettingsProps = {
   instanceId: string
   settingsMode?: boolean
}

export const RefIntegrationSettings = ({settingsMode, instanceId}: RefIntegrationSettingsProps) => {
   const {data} = useRefIntegrationSettingsQuery({
      variables: {
         instanceId
      }
   });

   if (!data) {
      return <LinearProgress/>
   }

   const tabs: PageTab[] = [{
      id: 'Messages',
      label: data?.IntegrationInstance?.name || 'Messages',
   }, {
      label: 'Settings'
   }]

   return <div>
      {settingsMode ? (
         <SettingsWithSetup instanceId={instanceId} wrapWithPageContent={false}/>
      ) : (
         <PageContentTabs onBack tabs={tabs} render={(tab) => {
            if (tab === 'Messages') {
               return <PageContent>
                  <MessagesEditor instanceId={instanceId}/>
               </PageContent>
            } else if (tab === 'Settings') {
               return <SettingsWithSetup instanceId={instanceId}/>
            } else {
               return <Box></Box>
            }
         }}/>
      )}
   </div>
}


type MessagesEditorProps = {
   instanceId: string
}

const MessagesEditor = ({instanceId}: MessagesEditorProps) => {
   const {data} = useRefIntegrationSettingsQuery({
      variables: {
         instanceId
      }
   });
   const [update] = useRefIntegrationMessageUpdateMutation()

   if (!data) {
      return <LinearProgress/>
   }

   const onToggleMessage = (message: EnumIntegrationConnectorMessages) => async (e: any, checked: boolean) => {
      await update({
         variables: {
            instanceId,
            message: message as any,
            enabled: checked
         },
         refetchQueries: [{
            query: RefIntegrationSettingsDocument,
            variables: {
               instanceId
            }
         }]
      })
   }

   return <Box>
      <TitleText type={"h2"}>API Messages supported</TitleText>
      <BodyText type={"body1"}>The {data?.IntegrationInstance?.name} integration supports the following messages. You
         can enable/disable messages to cutomize the integration.</BodyText>
      {data.IntegrationInstance?.connector?.messages?.map((message: any) => {
         const config = data.IntegrationInstance?.configs?.find((config: any) => config?.message as string === message as string);
         return <Box key={instanceId + '_' + message}>
            <FormControlLabel
               disabled={SystemMessages.includes(message as EnumIntegrationConnectorMessages)}
               control={<Checkbox color="primary"/>}
               checked={config?.enabled}
               label={message}
               onChange={onToggleMessage(message!)}
            />
         </Box>
      })}
   </Box>
}

type SettingsWithSetupProps = {
   instanceId: string
   wrapWithPageContent?: boolean
}

const SettingsWithSetup = ({instanceId, wrapWithPageContent = true}: SettingsWithSetupProps) => {
   const {data} = useRefIntegrationSettingsQuery({
      variables: {instanceId}
   });

   const [open, setOpen] = React.useState(false);
   const [email, setEmail] = React.useState('');
   const [result, setResult] = React.useState<string | null>(null);
   const [setup, {loading}] = useRefConsumerActivitySetupMutation();
   const [save] = useRefIntegrationSettingUpdateMutation();

   if (!data) {
      return <LinearProgress/>;
   }

   const hasConsumerActivity = (data?.IntegrationInstance?.connector?.messages || [])
      .includes(EnumIntegrationConnectorMessages.CONSUMERACTIVITY as any);

   const onOpen = () => {
      setResult(null);
      setEmail('');
      setOpen(true);
   };

   const onClose = () => {
      setOpen(false);
   };

   const onInitialize = async () => {
      setResult(null);
      const res = await setup({variables: {instanceId, email}});
      const msg = res.data?.ConsumerActivitySetup || 'Initialization completed';
      setResult(msg);
   };

   const setupButton = hasConsumerActivity ? (
      <Button variant={"contained"} onClick={onOpen}>Setup by mail</Button>
   ) : undefined;

   const onSave = async (settings: SettingsLine[]) => {
      await SavingUI.process(async () => {
         await save({
            variables: {
               instanceId,
               input: settings.map(setting => ({
                  key: setting.key,
                  value: setting.value
               }))
            },
            refetchQueries: [{
               query: RefIntegrationSettingsDocument,
               variables: {instanceId}
            }]
         })
      }, 'Saving settings to connector')
   }

   const content = <>
      <SettingsEditor onSave={onSave}
                      fields={(data?.IntegrationInstance?.connector?.fields || []) as IntegrationConnectorFields[]}
                      settings={data?.IntegrationInstance?.settings?.map((setting: any) => ({
                         key: setting!.key,
                         value: setting!.value
                      })) || []}
      >
         {setupButton}
      </SettingsEditor>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
         <DialogTitle>Initialize {data?.IntegrationInstance?.connector?.name} by email</DialogTitle>
         <DialogContent>
            <BodyText type={"body1"}>
               Please enter the email of the consumer that will receive initialization data
            </BodyText>
            <Box sx={{mt: 2}}>
               <TextField
                  type={"email"}
                  label={"Consumer email"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  disabled={loading}
               />
            </Box>
            {loading && <Box sx={{mt: 2, display: 'flex', alignItems: 'center', gap: 1}}>
               <CircularProgress size={20}/>
               <BodyText type={"body2"}>Initializing…</BodyText>
            </Box>}
            {result && <Box sx={{mt: 2}}>
               <Alert severity={"success"}>{result}</Alert>
            </Box>}
         </DialogContent>
         <DialogActions>
            <Button onClick={onClose} disabled={loading}>Close</Button>
            <Button variant={"contained"} onClick={onInitialize} disabled={!email || loading}>Initialize</Button>
         </DialogActions>
      </Dialog>
   </>;

   return wrapWithPageContent ? <PageContent>{content}</PageContent> : content
}
